import { useState, useCallback, useRef, useEffect } from 'react';
import { getChatHistory } from '../services/chat';

export const useVirtualChatHistory = (userId = null) => {
  const [allChats, setAllChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const cacheNamespace = userId ? `user-${userId}` : 'anonymous';
  const INITIAL_CHATS_CACHE_KEY = `velox-initial-chats-${cacheNamespace}`;
  const CHAT_HISTORY_PREFIX = `${cacheNamespace}-chat-history`;
  
  // Cache for chat history
  const chatHistoryCache = useRef(new Map());
  const CHAT_HISTORY_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

  // Local storage cache for initial chats (latest 10)
  const INITIAL_CHATS_CACHE_TIME = 10 * 60 * 1000; // 10 minutes

  // Check if cache is valid
  const isCacheValid = useCallback((timestamp, cacheTime) => {
    return timestamp && (Date.now() - timestamp < cacheTime);
  }, []);

  // Local storage cache functions
  const getInitialChatsFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(INITIAL_CHATS_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        if (isCacheValid(data.timestamp, INITIAL_CHATS_CACHE_TIME)) {
          return data.chats;
        }
      }
    } catch (error) {
      console.warn('Error reading initial chats from cache:', error);
    }
    return null;
  }, [isCacheValid, INITIAL_CHATS_CACHE_KEY, INITIAL_CHATS_CACHE_TIME]);

  const setInitialChatsToCache = useCallback((chats) => {
    try {
      const data = {
        chats: chats.slice(0, 10), // Only cache latest 10 chats
        timestamp: Date.now()
      };
      localStorage.setItem(INITIAL_CHATS_CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Error caching initial chats:', error);
    }
  }, [INITIAL_CHATS_CACHE_KEY]);

  const clearInitialChatsCache = useCallback(() => {
    try {
      localStorage.removeItem(INITIAL_CHATS_CACHE_KEY);
    } catch (error) {
      console.warn('Error clearing initial chats cache:', error);
    }
  }, [INITIAL_CHATS_CACHE_KEY]);

  // Load initial chat history
  const loadInitialChatHistory = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // First, try to load from localStorage cache for instant display
      if (!forceRefresh) {
        const cachedChats = getInitialChatsFromCache();
        if (cachedChats && cachedChats.length > 0) {
          setAllChats(cachedChats);
          setIsLoading(false);
          return; // Don't do background refresh - it's causing slowness
        }
      }

      // Check in-memory cache
      const cacheKey = `${CHAT_HISTORY_PREFIX}-initial`;
      const cachedData = chatHistoryCache.current.get(cacheKey);
      
      if (!forceRefresh && cachedData && isCacheValid(cachedData.timestamp, CHAT_HISTORY_CACHE_TIME)) {
        setAllChats(cachedData.data.chats);
        setHasMore(cachedData.data.pagination?.hasMore ?? false);
        setCurrentPage(cachedData.data.pagination?.page ?? 1);
        setIsLoading(false);
        
        // Update localStorage cache
        setInitialChatsToCache(cachedData.data.chats);
        return;
      }

      const result = await getChatHistory(1, 20);
      
      if (Array.isArray(result.chats)) {
        chatHistoryCache.current.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
        
        setAllChats(result.chats);
        // Use safe navigation and fallbacks for pagination
        const pagination = result.pagination || {};
        setHasMore(pagination.hasMore ?? (result.chats.length >= 20));
        setCurrentPage(pagination.page ?? 1);
        
        // Cache to localStorage for next time
        setInitialChatsToCache(result.chats);
      } else {
        throw new Error('Invalid chat history response: expected an array of chats');
      }
    } catch (error) {
      console.error('Error loading initial chat history:', error);
      setError(error.message || 'Failed to load chat history');
      setAllChats([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [isCacheValid, CHAT_HISTORY_CACHE_TIME, getInitialChatsFromCache, setInitialChatsToCache, CHAT_HISTORY_PREFIX]);

  // Load more chats for virtual scrolling
  const loadMoreChats = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    
    try {
      const nextPage = currentPage + 1;
      const cacheKey = `${CHAT_HISTORY_PREFIX}-page-${nextPage}`;
      const cachedData = chatHistoryCache.current.get(cacheKey);
      
      if (cachedData && isCacheValid(cachedData.timestamp, CHAT_HISTORY_CACHE_TIME)) {
        console.log(`Using cached chat history for page ${nextPage}`);
        setAllChats(prev => [...prev, ...cachedData.data.chats]);
        setHasMore(cachedData.data.pagination?.hasMore ?? false);
        setCurrentPage(cachedData.data.pagination?.page ?? nextPage);
        setIsLoadingMore(false);
        return;
      }

      console.log(`Fetching fresh chat history for page ${nextPage}...`);
      const result = await getChatHistory(nextPage, 20);
      
      if (Array.isArray(result.chats)) {
        chatHistoryCache.current.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
        
        setAllChats(prev => [...prev, ...result.chats]);
        // Use safe navigation and fallbacks for pagination
        const pagination = result.pagination || {};
        setHasMore(pagination.hasMore ?? (result.chats.length >= 20));
        setCurrentPage(pagination.page ?? nextPage);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more chats:', error);
      setError(error.message || 'Failed to load more chats');
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, currentPage, isCacheValid, CHAT_HISTORY_CACHE_TIME, CHAT_HISTORY_PREFIX]);

  // Add new chat to the beginning of the list
  const addNewChat = useCallback((newChat) => {
    setAllChats(prev => [newChat, ...prev]);
    
    // Clear in-memory cache to ensure fresh data on next load
    chatHistoryCache.current.clear();
  }, []);

  // Remove chat from the list
  const removeChat = useCallback((chatId) => {
    setAllChats(prev => prev.filter(chat => chat.id !== chatId));
    
    // Clear in-memory cache to ensure fresh data on next load
    chatHistoryCache.current.clear();
  }, []);

  // Update chat in the list
  const updateChat = useCallback((updatedChat) => {
    setAllChats(prev => 
      prev.map(chat => 
        chat.id === updatedChat.id ? { ...chat, ...updatedChat } : chat
      )
    );
  }, []);

  // Refresh chat history
  const refreshChatHistory = useCallback(() => {
    chatHistoryCache.current.clear();
    clearInitialChatsCache();
    setCurrentPage(1);
    setHasMore(true);
    loadInitialChatHistory(true);
  }, [loadInitialChatHistory, clearInitialChatsCache]);

  useEffect(() => {
    chatHistoryCache.current.clear();
    setAllChats([]);
    setCurrentPage(1);
    setHasMore(true);
  }, [cacheNamespace]);

  return {
    allChats,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadInitialChatHistory,
    loadMoreChats,
    addNewChat,
    removeChat,
    updateChat,
    refreshChatHistory,
    clearInitialChatsCache
  };
};
